import {
    isLeadingGapOnly,
    RequirementKey,
} from "@common/helpers/documentFulfillment";
import type { CoverageRow } from "@common/helpers/documentFulfillment";
import type { MigrationRequiredPeriod } from "@common/helpers/migrationRequiredPeriod";
import type { MigrationFileType } from "@common/schemas/accounting-integration.schema";
import {
    type FinalizeWarning,
    STARTS_AFTER_MIGRATION_MESSAGE,
    STARTS_BEFORE_MIGRATION_MESSAGE,
    type MigrationDoc,
    type MigrationMatchedRow,
    type MigrationStatusText,
    ROLE_LABELS,
} from "./migrationFileMatching.types";
import { isMigrationRowReadyForFinalize } from "./migrationExtractionWarnings";
import { toFileType } from "./migrationFileMatchingConverters";

export function basisLabels(keys: RequirementKey[]): string[] {
    return keys.map((key) => ROLE_LABELS[toFileType(key)]);
}

const BASIS_NOUN: Record<MigrationFileType, string> = {
    coa: ROLE_LABELS.coa,
    cashGl: "Cash",
    accrualGl: "Accrual",
    unknownGl: ROLE_LABELS.unknownGl,
};

// A basis the user omitted entirely won't be imported at all. A provided basis
// with a coverage gap is a hard blocker (surfaced as a flagged row), not a
// warning, so it never reaches here.
export function missingBasisWarning(key: RequirementKey): FinalizeWarning {
    const noun = BASIS_NOUN[toFileType(key)];
    return {
        kind: "missingBasis",
        message: `${noun} books won't be imported.`,
    };
}

function isGlBasisRole(role: MigrationFileType): boolean {
    return role === "cashGl" || role === "accrualGl";
}

function earliestReadyStartByBasis({
    rows,
    docsById,
}: {
    rows: MigrationMatchedRow[];
    docsById: Map<number, MigrationDoc>;
}): Map<MigrationFileType, string> {
    const earliestByBasis = new Map<MigrationFileType, string>();
    for (const row of rows) {
        if (
            !isMigrationRowReadyForFinalize(row.status) ||
            row.documentId == null
        ) {
            continue;
        }
        if (!isGlBasisRole(row.role)) {
            continue;
        }
        const docStart = docsById.get(row.documentId)?.period?.start;
        if (!docStart) {
            continue;
        }
        const current = earliestByBasis.get(row.role);
        if (current === undefined || docStart < current) {
            earliestByBasis.set(row.role, docStart);
        }
    }
    return earliestByBasis;
}

/**
 * Earliest coverage start across the GL files that are ready to migrate — the
 * start date the uploaded data implies, offered as a one-click replacement for
 * the declared migration start.
 */
export function earliestReadyGlStart({
    matchedRows,
    docsById,
}: {
    matchedRows: MigrationMatchedRow[];
    docsById: Map<number, MigrationDoc>;
}): string | null {
    let earliestStart: string | null = null;
    for (const start of earliestReadyStartByBasis({
        rows: matchedRows,
        docsById,
    }).values()) {
        if (earliestStart === null || start < earliestStart) {
            earliestStart = start;
        }
    }
    return earliestStart;
}

function basisMigrationStartWarning(
    basis: MigrationFileType,
    message: MigrationStatusText,
): string {
    const detail =
        message.tooltip.charAt(0).toLowerCase() + message.tooltip.slice(1);
    return `${ROLE_LABELS[basis]} ${detail}`;
}

function hasLeadingUncoveredMonth({
    fulfillmentRows,
    basis,
    requiredPeriod,
}: {
    fulfillmentRows: CoverageRow[];
    basis: MigrationFileType;
    requiredPeriod: MigrationRequiredPeriod;
}): boolean {
    return fulfillmentRows.some(
        (row) =>
            row.requirementKey === basis &&
            row.status === "awaiting" &&
            isLeadingGapOnly(row, requiredPeriod),
    );
}

// "Starts after" uses fulfillment's leading-gap rows (month coverage); "starts
// before" still compares the original document start day.
export function collectMigrationStartWarnings({
    matchedRows,
    fulfillmentRows,
    docsById,
    requiredPeriod,
}: {
    matchedRows: MigrationMatchedRow[];
    fulfillmentRows: CoverageRow[];
    docsById: Map<number, MigrationDoc>;
    requiredPeriod: MigrationRequiredPeriod | null;
}): FinalizeWarning[] {
    if (requiredPeriod === null || requiredPeriod.start === null) {
        return [];
    }
    const migrationStart = requiredPeriod.start;
    const earliestStartByBasis = earliestReadyStartByBasis({
        rows: matchedRows,
        docsById,
    });
    const warnings: FinalizeWarning[] = [];

    for (const [basis, docStart] of earliestStartByBasis) {
        if (docStart < migrationStart) {
            warnings.push({
                kind: "startDate",
                message: basisMigrationStartWarning(
                    basis,
                    STARTS_BEFORE_MIGRATION_MESSAGE,
                ),
            });
            continue;
        }
        if (
            hasLeadingUncoveredMonth({
                fulfillmentRows,
                basis,
                requiredPeriod,
            })
        ) {
            warnings.push({
                kind: "startDate",
                message: basisMigrationStartWarning(
                    basis,
                    STARTS_AFTER_MIGRATION_MESSAGE,
                ),
            });
        }
    }

    return warnings;
}
